import { type ReactNode } from 'react'
import {
  ModalSheet,
  ModalSheetContent,
  ModalSheetContentBlock,
  ModalSheetContentDescription,
  ModalSheetContentIllustration,
  ModalSheetContentTitle,
  ModalSheetFooter,
  ModalSheetFooterButtonGroup,
  ModalSheetFooterSpacer,
  ModalSheetHeader,
} from '@/components/ModalSheet'
import { Button } from '@/components/Button'
import { Form } from '@/components/Form'
import { byDesign } from '@/infra/design'
import imgMissingRolesUrl from '@/assets/misc/img_missing-roles.svg'
import WorkspaceRolesIllustration from '@/components/SpotIllustration/WorkspaceRolesIllustration'

export interface EditUserRoleDialogProps {
  showing: boolean
  onClose: () => void
  /**
   * The single user being edited, or `null` when editing a selection.
   */
  selectedUserName: string | null
  selectedUserCount: number
  workspaceName: string
  onSubmit: () => void
  isSubmitPending: boolean
  /**
   * The role field.
   */
  children: ReactNode
}

export function EditUserRoleDialog({
  showing,
  onClose,
  selectedUserName,
  selectedUserCount,
  workspaceName,
  onSubmit,
  isSubmitPending,
  children,
}: EditUserRoleDialogProps) {
  return (
    <ModalSheet
      showing={showing}
      onRequestClose={isSubmitPending ? undefined : onClose}
    >
      <ModalSheetHeader withCloseButton />
      <ModalSheetContent>
        <ModalSheetContentBlock>
          <ModalSheetContentIllustration
            src={imgMissingRolesUrl}
            illustration={byDesign(undefined, WorkspaceRolesIllustration)}
            alt="Illustration of switching on a toggle"
          />

          <ModalSheetContentTitle>Edit user role</ModalSheetContentTitle>

          <ModalSheetContentDescription>
            Assign a role for
            {selectedUserName !== null ? (
              <b> {selectedUserName} </b>
            ) : (
              <b> {selectedUserCount} selected users </b>
            )}
            in workspace <b>{workspaceName}</b>.
          </ModalSheetContentDescription>
        </ModalSheetContentBlock>
        <ModalSheetContentBlock>
          <Form id="editUserRole" onSubmit={onSubmit}>
            {children}
          </Form>
        </ModalSheetContentBlock>
      </ModalSheetContent>
      <ModalSheetFooter>
        <ModalSheetFooterSpacer />
        <ModalSheetFooterButtonGroup>
          <Button
            label="Close"
            variant="default"
            onPress={onClose}
            isDisabled={isSubmitPending}
          />
          <Button
            label="Apply"
            variant="primary"
            testId="edit-user-role-apply-button"
            onPress={onSubmit}
            isDisabled={isSubmitPending}
            isPending={isSubmitPending}
          />
        </ModalSheetFooterButtonGroup>
      </ModalSheetFooter>
    </ModalSheet>
  )
}
